import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

let openSheetCount = 0;

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  closeLabel?: string;
  children: ReactNode;
}

/**
 * Escape/backdrop close assumes this is the topmost overlay; callers must not
 * stack sheets without handling Escape ordering.
 */
export default function BottomSheet({
  open,
  onClose,
  title,
  closeLabel,
  children,
}: BottomSheetProps) {
  const { t } = useTranslation("common");
  const resolvedCloseLabel = closeLabel ?? t("close");
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    openSheetCount += 1;
    document.body.style.overflow = "hidden";
    sheetRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      openSheetCount -= 1;
      if (openSheetCount === 0) {
        document.body.style.overflow = "";
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        data-testid="bottom-sheet-backdrop"
        aria-hidden="true"
        className="fixed inset-0 z-40 bg-black bg-opacity-50"
        onClick={onClose}
      />
      <div
        ref={sheetRef}
        role={title ? "dialog" : undefined}
        aria-modal={title ? "true" : undefined}
        aria-label={title}
        tabIndex={-1}
        data-testid="bottom-sheet"
        className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col rounded-t-2xl border-t border-[#1a1a2e] bg-[#0d0d14] shadow-2xl focus:outline-none"
      >
        <div className="flex justify-center pt-2">
          <div className="h-1 w-10 rounded-full bg-[#374151]" />
        </div>
        {title && (
          <div className="flex items-center justify-between px-4 pb-2 pt-2">
            <h3 className="font-semibold text-[#e2e8f0]">{title}</h3>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-2 text-[#4b5563] transition-colors hover:bg-[#1f2937] hover:text-[#94a3b8]"
              aria-label={resolvedCloseLabel}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <div
          className="min-h-0 flex-1 overscroll-contain overflow-y-auto px-4"
          style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        >
          {children}
        </div>
      </div>
    </>
  );
}
